import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Check, CircleNotch, Globe, WarningCircle } from '@phosphor-icons/react';
import { config } from '@/lib/config';
import { tenantApi, type SubdomainAvailability } from '@/lib/api';
import { SUBDOMAIN_RE, type OnboardingFormValues } from '../schema';

type LocalStatus =
  | { kind: 'idle' }
  | { kind: 'invalid' }
  | { kind: 'checking' }
  | { kind: 'available'; value: string }
  | { kind: 'unavailable'; reason: SubdomainAvailability['reason']; value: string };

interface SubdomainStepProps {
  onAvailabilityChange: (available: boolean) => void;
}

export function SubdomainStep({ onAvailabilityChange }: SubdomainStepProps) {
  const form = useFormContext<OnboardingFormValues>();
  const value = form.watch('subdomain');
  const slug = value.trim().toLowerCase();

  const [status, setStatus] = useState<LocalStatus>({ kind: 'idle' });
  const reqIdRef = useRef(0);

  useEffect(() => {
    form.clearErrors('subdomain');
    if (slug.length === 0) {
      setStatus({ kind: 'idle' });
      return;
    }
    if (!SUBDOMAIN_RE.test(slug)) {
      setStatus({ kind: 'invalid' });
      return;
    }
    setStatus({ kind: 'checking' });
    const reqId = ++reqIdRef.current;
    const handle = window.setTimeout(() => {
      tenantApi
        .checkSubdomain(slug)
        .then((res) => {
          if (reqId !== reqIdRef.current) return;
          if (res.available) {
            setStatus({ kind: 'available', value: slug });
          } else {
            setStatus({ kind: 'unavailable', reason: res.reason, value: slug });
            form.setError('subdomain', { message: reasonMessage(res.reason) });
          }
        })
        .catch(() => {
          if (reqId !== reqIdRef.current) return;
          setStatus({ kind: 'idle' });
        });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [slug, form]);

  useEffect(() => {
    onAvailabilityChange(status.kind === 'available' && status.value === slug);
  }, [status, slug, onAvailabilityChange]);

  return (
    <div>
      <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
        Your address
      </label>
      <div className="group flex items-stretch overflow-hidden rounded-lg border border-line bg-board transition-colors focus-within:border-ink">
        <span className="flex items-center pl-4 pr-2 text-ink-faint">
          <Globe size={18} />
        </span>
        <input
          {...form.register('subdomain', {
            setValueAs: (v: string) => v.toLowerCase(),
          })}
          placeholder="your-name"
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          className="flex-1 bg-transparent py-3.5 pr-2 font-mono text-[15px] text-ink placeholder:text-ink-faint focus:outline-none"
        />
        <span className="flex items-center border-l border-line bg-rail px-4 font-mono text-[14px] text-ink-muted">
          {config.rootDomain ? `.${config.rootDomain}` : ' (your handle)'}
        </span>
      </div>

      <StatusLine status={status} />

      <div className="mt-8 rounded-lg border border-line bg-rail p-4 text-[13px] leading-relaxed text-ink-muted">
        <p>
          <span className="font-medium text-ink">Rules.</span> 3–30 characters. Lowercase
          letters, numbers, and hyphens only. No leading or trailing hyphen. You can move to
          a custom domain any time.
        </p>
      </div>
    </div>
  );
}

function reasonMessage(reason: SubdomainAvailability['reason']): string {
  switch (reason) {
    case 'taken':
      return 'This subdomain is already taken';
    case 'reserved':
      return 'This subdomain is reserved';
    case 'invalid_format':
      return 'Invalid format';
    case 'empty':
      return 'Enter a subdomain';
    default:
      return 'This subdomain is not available';
  }
}

function StatusLine({ status }: { status: LocalStatus }) {
  if (status.kind === 'idle') {
    return (
      <p className="mt-2 h-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
        Start typing to check availability
      </p>
    );
  }
  if (status.kind === 'invalid') {
    return (
      <p className="mt-2 flex h-5 items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-red-600">
        <WarningCircle size={12} weight="fill" /> Invalid format
      </p>
    );
  }
  if (status.kind === 'checking') {
    return (
      <p className="mt-2 flex h-5 items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
        <CircleNotch size={12} className="animate-spin" /> Checking availability…
      </p>
    );
  }
  if (status.kind === 'available') {
    return (
      <p className="mt-2 flex h-5 items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-700">
        <Check size={12} weight="bold" /> Available — we'll claim it on finish
      </p>
    );
  }
  return (
    <p className="mt-2 flex h-5 items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-red-600">
      <WarningCircle size={12} weight="fill" /> {reasonMessage(status.reason)}
    </p>
  );
}
